import { useCallback } from 'react';
import * as THREE from 'three';
import type { ViewType, ViewportControls, WorkspaceState } from '../../utils/workspace.types';
import type { STAADWorkspaceModernProps } from '../../utils/workspace.types';

/**
 * Viewport controls hook - handles camera positioning and view management
 * Extracted from STAADWorkspace3D to maintain exact behavior
 */
export const useViewportControls = (
  state: WorkspaceState,
  _props: STAADWorkspaceModernProps,
  _nodes: any[],
  _beams: any[]
): ViewportControls => {
  // const lastOptimalDistance = useRef<number>(50);
  // const isInitialMount = useRef(true);

  // Calculate optimal camera distance
  const calculateOptimalCameraDistance = useCallback((nodes: any[], beams: any[]): number => {
    const box = new THREE.Box3();
    
    // Add nodes to bounding box
    nodes.forEach(node => {
      if (node && typeof node.x === 'number' && typeof node.y === 'number' && typeof node.z === 'number') {
        box.expandByPoint(new THREE.Vector3(node.x, node.y, node.z));
      }
    });
    
    // Add beam endpoints to bounding box
    beams.forEach(beam => {
      if (beam && beam.startNode && beam.endNode) {
        box.expandByPoint(new THREE.Vector3(beam.startNode.x, beam.startNode.y, beam.startNode.z));
        box.expandByPoint(new THREE.Vector3(beam.endNode.x, beam.endNode.y, beam.endNode.z));
      }
    });
    
    if (box.isEmpty()) {
      return 50; // Default distance if no objects
    }
    
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    return maxDim * 2; // Multiply by 2 for better visibility
  }, []);

  // Switch camera view
  const switchView = useCallback((view: ViewType) => {
    // This would be implemented with Three.js camera positioning
    // For now, just update the plane info based on view
    const newPlaneInfo = {
      currentPlane: view === 'top' ? 'XY' : view === 'front' ? 'XZ' : view === 'side' ? 'YZ' : 'XY',
      viewOrientation: view.charAt(0).toUpperCase() + view.slice(1),
      coordinateSystem: 'Global',
      elevation: state.planeInfo.elevation
    };
    
    // Update state through callbacks would be handled by parent component
    //console.log('Switching to view:', view, 'with plane info:', newPlaneInfo);
  }, [state.planeInfo.elevation]);

  // Get current plane based on camera position
  const getCurrentPlane = useCallback((cameraPosition: THREE.Vector3, cameraTarget: THREE.Vector3): string => {
    const direction = cameraPosition.clone().sub(cameraTarget).normalize();
    
    // Determine primary viewing direction
    const absX = Math.abs(direction.x);
    const absY = Math.abs(direction.y);
    const absZ = Math.abs(direction.z);
    
    if (absY > absX && absY > absZ) {
      return 'XZ'; // Looking from top/bottom
    } else if (absX > absZ) {
      return 'YZ'; // Looking from side
    } else {
      return 'XY'; // Looking from front/back
    }
  }, []);

  // Get view orientation
  const getViewOrientation = useCallback((cameraPosition: THREE.Vector3, cameraTarget: THREE.Vector3): string => {
    const direction = cameraPosition.clone().sub(cameraTarget).normalize();
    
    // Simple heuristic to determine view orientation
    if (Math.abs(direction.y) > 0.8) {
      return direction.y > 0 ? 'Top' : 'Bottom';
    } else if (Math.abs(direction.z) > 0.8) {
      return direction.z > 0 ? 'Front' : 'Back';
    } else if (Math.abs(direction.x) > 0.8) {
      return direction.x > 0 ? 'Right' : 'Left';
    } else {
      return 'Isometric';
    }
  }, []);

  // Update plane info based on current camera position
  const updatePlaneInfo = useCallback(() => {
    // This would be implemented with actual camera position when integrated
    // For now, maintain current plane info
    //console.log('Updating plane info for current camera position');
  }, []);

  return {
    switchView,
    getCurrentPlane,
    getViewOrientation,
    updatePlaneInfo,
    calculateOptimalCameraDistance
  };
};
